import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const FacultyProfileModal = ({ 
  isOpen, 
  onClose, 
  facultyId, 
  facultyName = 'Faculty' 
}) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isLikelyId = (val) => {
    if (!val || typeof val !== 'string') return false;
    if (/^[0-9a-fA-F]{24}$/.test(val)) return true;
    if (/^(FACU|STAF|TYN|TYON)[A-Z0-9]*\d+/.test(val)) return true;
    if (/^[A-Z0-9_-]{8,}$/.test(val) && /\d/.test(val)) return true;
    return false;
  };

  const extractName = (val) => {
    if (!val && val !== 0) return null;
    if (typeof val === 'string') {
      if (isLikelyId(val)) return null;
      return val;
    }
    if (Array.isArray(val)) {
      const names = val.map(item => extractName(item)).filter(Boolean);
      return names.length ? names.join(', ') : null;
    }
    if (typeof val === 'object') {
      if (val.fullName) return extractName(val.fullName);
      if (val.name) return extractName(val.name);
      if (val.firstName || val.lastName) {
        const f = val.firstName || '';
        const l = val.lastName || '';
        const combined = `${f} ${l}`.trim();
        return combined || null;
      }
      return null;
    }
    return String(val);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      if (dateValue._seconds) {
        const date = new Date(dateValue._seconds * 1000);
        return date.toLocaleDateString();
      }
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleDateString();
      }
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) return date.toLocaleDateString();
      return null;
    } catch {
      return null;
    }
  };

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/faculty/profile/${facultyId}`);
      if (response.data?.success) {
        setProfileData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error loading profile');
    } finally {
      setLoading(false);
    }
  }, [facultyId]);

  useEffect(() => {
    if (isOpen && facultyId) fetchProfileData();
  }, [isOpen, facultyId, fetchProfileData]);

  if (!isOpen) return null;

  const personal = profileData?.personalInfo || {};
  const system = profileData?.systemInfo || {};

  const resolvedName = extractName(personal.fullName) || `${extractName(personal.firstName) || ''} ${extractName(personal.lastName) || ''}`.trim() || facultyName;
  const initialLetter = resolvedName.charAt(0).toUpperCase() || 'F';
  const accountStatusStr = (extractName(system.accountStatus) || system.accountStatus || '').toLowerCase();

  const getStatusBadgeStyles = (status) => {
    if (status === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'pending') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (status === 'suspended') return 'bg-rose-50 text-rose-700 border-rose-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <div 
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-end md:justify-center items-end md:items-center z-50 p-0 md:p-4 transition-opacity duration-300"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full md:max-w-xl rounded-t-2xl md:rounded-xl flex flex-col shadow-2xl max-h-[92vh] md:max-h-[85vh] overflow-hidden animate-in slide-in-from-bottom duration-300 md:animate-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Mobile Drag/Notch indicator */}
        <div className="flex justify-center py-2 md:hidden bg-gradient-to-r from-indigo-600 to-indigo-700 shrink-0">
          <div className="w-12 h-1 bg-white/20 rounded-full"></div>
        </div>

        {/* Hero Cover Header Backdrop Section */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-indigo-700 to-indigo-800 text-white px-5 pt-6 pb-14 shrink-0">
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 p-2 bg-slate-900/10 hover:bg-white/10 text-white/90 hover:text-white rounded-full transition-colors z-10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="text-white/70 text-xs font-bold tracking-wider uppercase">Institutional Member</div>
          <h3 className="text-xl font-black mt-0.5 tracking-tight truncate max-w-[85%]">{resolvedName}</h3>
        </div>

        {/* Overlapping Avatar Profile Element */}
        <div className="px-6 relative shrink-0 h-10 bg-slate-50/50">
          <div className="absolute -top-10 left-6 flex items-end gap-3.5">
            {personal.profilePicture ? (
              <img 
                src={personal.profilePicture} 
                alt="Profile Avatar" 
                className="w-20 h-20 rounded-full object-cover border-4 border-white bg-white shadow-md shadow-slate-200" 
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-slate-100 border-4 border-white shadow-md shadow-slate-200 flex items-center justify-center text-indigo-700 font-extrabold text-2xl">
                {initialLetter}
              </div>
            )}
            
            {accountStatusStr && (
              <div className="mb-1.5">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border capitalize tracking-wide shadow-sm ${getStatusBadgeStyles(accountStatusStr)}`}>
                  <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    accountStatusStr === 'active' ? 'bg-emerald-500' : accountStatusStr === 'pending' ? 'bg-amber-500' : 'bg-rose-500'
                  }`}></span>
                  {extractName(system.accountStatus) || system.accountStatus}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Primary Data Content Scroll Matrix */}
        <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50 space-y-5">
          {loading && (
            <div className="text-center py-12 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent mb-3"></div>
              <div className="text-xs font-semibold text-slate-500 tracking-wide">Syncing profile matrix...</div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3.5 text-xs font-medium flex items-center gap-2">
              <svg className="w-4 h-4 text-rose-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          )}

          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
              {/* Basic Meta Group */}
              <div className="bg-white border border-slate-200/60 rounded-xl p-4 shadow-sm space-y-3.5">
                <h4 className="text-[11px] font-bold tracking-wider text-indigo-600 uppercase border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Basic Profile Records
                </h4>
                
                <div className="space-y-3">
                  {extractName(personal.fullName) && (
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Legal Designation</div>
                      <div className="text-sm font-semibold text-slate-900 mt-0.5">{extractName(personal.fullName)}</div>
                    </div>
                  )}

                  {extractName(personal.email) && (
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Digital Destination</div>
                      <div className="text-sm font-semibold text-indigo-600 mt-0.5 break-all">{extractName(personal.email)}</div>
                    </div>
                  )}

                  {(personal.phone || personal.primaryPhone) && (
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Contact Identifier</div>
                      <div className="text-sm font-semibold text-slate-900 mt-0.5">{extractName(personal.phone || personal.primaryPhone)}</div>
                    </div>
                  )}

                  {formatDate(personal.dateOfBirth) && (
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Birth Anniversary</div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5 font-mono">{formatDate(personal.dateOfBirth)}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Account System Info Group */}
              <div className="bg-white border border-slate-200/60 rounded-xl p-4 shadow-sm space-y-3.5">
                <h4 className="text-[11px] font-bold tracking-wider text-slate-600 uppercase border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  System Security Audit
                </h4>
                
                <div className="space-y-3">
                  {formatDate(system.createdAt) && (
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Onboarding Timestamp</div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5 font-mono">{formatDate(system.createdAt)}</div>
                    </div>
                  )}

                  {formatDate(system.lastLogin) && (
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Session Verification</div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5 font-mono">{formatDate(system.lastLogin)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Interaction Container */}
        <div className="border-t border-slate-100 p-4 bg-white flex justify-end shrink-0 pb-6 md:pb-4 shadow-inner">
          <button 
            onClick={onClose} 
            className="w-full md:w-auto px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold tracking-wide rounded-xl shadow-md transition-all uppercase"
          >
            Dismiss View
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacultyProfileModal;